import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Students from "@/pages/Students";
import Spp from "@/pages/Spp";
import UsersPage from "@/pages/Users";
import Schools from "@/pages/Schools";
import ParentHome from "@/pages/ParentHome";
import Savings from "@/pages/Savings";
import Bills from "@/pages/Bills";
import Reports from "@/pages/Reports";
import ActivityLogs from "@/pages/ActivityLogs";
import WaGateway from "@/pages/WaGateway";
import Settings from "@/pages/Settings";
import Expenses from "@/pages/Expenses";
import ParentAnnouncements from "@/pages/ParentAnnouncements";
import ParentPay from "@/pages/ParentPay";
import ParentSavings from "@/pages/ParentSavings";
import PaymentConfirmations from "@/pages/PaymentConfirmations";
import Subscriptions from "@/pages/Subscriptions";
import Announcements from "@/pages/Announcements";

function FullPageLoading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50" data-testid="loading-screen">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullPageLoading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function HomeByRole() {
  const { user } = useAuth();
  if (user.role === "orang_tua") return <Navigate to="/anak" replace />;
  return <Dashboard />;
}

function RoleRoute({ allow, children }) {
  const { user } = useAuth();
  if (user === null) return <FullPageLoading />;
  if (!allow.includes(user.role)) {
    return <Navigate to={user.role === "orang_tua" ? "/anak" : "/"} replace />;
  }
  return children;
}

const STAFF = ["super_admin", "kepala_sekolah", "bendahara"];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <Protected>
                <AppShell />
              </Protected>
            }
          >
            <Route index element={<HomeByRole />} />
            <Route path="siswa" element={<RoleRoute allow={STAFF}><Students /></RoleRoute>} />
            <Route path="spp" element={<RoleRoute allow={STAFF}><Spp /></RoleRoute>} />
            <Route path="tabungan" element={<RoleRoute allow={STAFF}><Savings /></RoleRoute>} />
            <Route path="tagihan" element={<RoleRoute allow={STAFF}><Bills /></RoleRoute>} />
            <Route path="kas" element={<RoleRoute allow={STAFF}><Expenses /></RoleRoute>} />
            <Route path="pemberitahuan" element={<RoleRoute allow={STAFF}><Announcements /></RoleRoute>} />
            <Route path="laporan" element={<RoleRoute allow={STAFF}><Reports /></RoleRoute>} />
            <Route path="log" element={<RoleRoute allow={STAFF}><ActivityLogs /></RoleRoute>} />
            <Route path="whatsapp" element={<RoleRoute allow={["super_admin", "bendahara"]}><WaGateway /></RoleRoute>} />
            <Route path="pengaturan" element={<Settings />} />
            <Route path="pengguna" element={<RoleRoute allow={["super_admin", "bendahara"]}><UsersPage /></RoleRoute>} />
            <Route path="sekolah" element={<RoleRoute allow={["super_admin"]}><Schools /></RoleRoute>} />
            <Route path="anak" element={<RoleRoute allow={["orang_tua"]}><ParentHome /></RoleRoute>} />
            <Route path="anak/pemberitahuan" element={<RoleRoute allow={["orang_tua"]}><ParentAnnouncements /></RoleRoute>} />
            <Route path="anak/bayar" element={<RoleRoute allow={["orang_tua"]}><ParentPay /></RoleRoute>} />
            <Route path="anak/tabungan" element={<RoleRoute allow={["orang_tua"]}><ParentSavings /></RoleRoute>} />
            <Route path="konfirmasi" element={<RoleRoute allow={STAFF}><PaymentConfirmations /></RoleRoute>} />
            <Route path="langganan" element={<RoleRoute allow={["super_admin"]}><Subscriptions /></RoleRoute>} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
